<template>
  <div
    v-if="premiumPacks"
    class="gallery-pack-buy-popup flex justify-evenly"
    :class="pack.pack_type"
  >
    <div
      class="gallery-pack-buy-popup-left flex justify-evenly items-center flex-col h-full flex-shrink-0"
    >
      <p class="popup-left-title text-36 text-texts-standard-default font-bold uppercase">
        {{ $replacePlaceholder($t('gallery.typePacks'), '{type}', $t(`gallery.${pack.pack_type}Pack`)) }}
      </p>

      <div class="popup-left-img" />

      <section class="popup-left-info relative">
        <p class="text-28 text-texts-standard-default">{{ $t('gallery.packContains') }}</p>

        <div class="popup-left-box relative flexing" :class="$isSsm ? 'revert-skew' : 'skew'">
          <section class="flexing w-full" :class="{ 'revert-skew': $isWsm }">
            <p class="text-36 text-texts-standard-important mr-1 font-bold">{{ pack.total_pieces }}</p>
            <app-icon icon-name="puzzle-52" />
          </section>
        </div>
      </section>

      <tippy theme="WSM2" placement="right" max-width="35rem" trigger="click">
        <app-button btn-type="primary" :btn-text="$t('button.detail')" />

        <template #content>
          <gallery-pack-tooltip
            :quantity="pack.total_pieces"
            :pieces="pack.pack_pieces"
            :title="tooltipText"
          />
        </template>
      </tippy>
    </div>

    <div class="gallery-pack-buy-popup-right flex flex-col justify-around h-full relative">
      <div v-for="(option, i) in packOptions" :key="i" class="popup-buy-box flex flex-col">
        <div class="popup-buy-box-top flex relative">
          <div class="flex">
            <p class="text-36 text-texts-standard-default font-bold uppercase">
              {{ $t('premiumGroup.buy') }}
            </p>

            <div
              v-if="!!option.parameters.best_value"
              class="popup-buy-box-best-value text-28 font-bold uppercase text-texts-standard-default flex items-center ml-4"
            >
              Best value
            </div>
          </div>

          <div
            v-if="option.parameters.percentage_off > 0"
            class="popup-buy-box-old-price text-32 text-texts-standard-default flexing"
          >
            <span>{{ $filters.$formatNumber(option.parameters.price_gems_original) }}</span>
            <app-main-icon class="ml-2" :icon-size="48" icon-name="gems" />
          </div>

          <div
            v-if="option.parameters.percentage_off > 0"
            class="popup-buy-box-discount absolute text-30 text-texts-standard-default"
          >
            {{ `${option.parameters.percentage_off}% Off` }}
          </div>
        </div>

        <div class="popup-buy-box-bottom relative flex">
          <div class="popup-buy-box-value flex items-center">
            <span class="text-36 font-bold text-texts-standard-default">{{
              `${option.parameters.amount}x`
            }}</span>

            <p class="text-texts-standard-default text-32 ml-4 uppercase">
              {{
                $replacePlaceholder($t('gallery.typePack'), '{type}', $t(`gallery.${pack.pack_type}Pack`))
              }}
            </p>
          </div>

          <app-multi-button1
            btn-id="buy-gallery-pack"
            btn-type="credit"
            :btn-text="$t('premiumGroup.buy')"
            :btn-count="option.parameters.price_gems"
            :popup-data="{ ...btnPopupData, btnValue: option.parameters.price_gems }"
            @action="goBuyPack(option.id, option.parameters.amount, pack.pack_name)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useGalleryPacksStore } from '@/store/pinia/gallery/packsStore'
import GalleryPackTooltip from '@/components/Gallery/GalleryPackTooltip.vue'
import type { PremiumPack } from '@/interfaces/premium/PremiumPack'
import type { GalleryPack } from '@/interfaces/gallery/Packs'
import { GalleryPackType } from '@/interfaces/gallery/enums'

export default defineComponent({
  name: 'GalleryPackPremiumPopup',

  components: { GalleryPackTooltip },

  props: {
    pack: {
      type: Object as PropType<GalleryPack>,
      required: true
    }
  },

  emits: ['close'],

  computed: {
    ...mapState(useGalleryPacksStore, ['premiumPacks']),

    packOptions(): PremiumPack[] {
      return this.premiumPacks?.filter(
        (a: PremiumPack): boolean => 
          a.parameters.type === this.pack.pack_name
      )
    },

    tooltipText(): string {
      return this.$replacePlaceholder(
        this.$t('gallery.typePack'),
        '{type}',
        this.$t(`gallery.${this.pack.pack_type}Pack`),
      )
    },

    btnPopupData(): any {
      return this.$filters.$confirmPopupData({
        title: this.$replacePlaceholder(
          this.$t('gallery.typePack'),
          '{type}',
          this.$t(`gallery.${this.pack.pack_type}Pack`),
        ),
        text: this.$t('premiumGroup.addOnConfirm'),
        btnId: 'btn-buy-gallery-pack',
        btnText: this.$t('premiumGroup.buy'),
        btnValue: 0,
      })
    }
  },

  created(): void {
    if (this.premiumPacks === null) this.loadPremiumData()
  },

  methods: {
    ...mapActions(useGalleryPacksStore, ['loadPremiumData', 'buyPack']),

    async goBuyPack(optionId: string, amount: number, packName: GalleryPackType): Promise<void> {
      await this.buyPack(optionId, amount, packName)
      this.$emit('close')
    }
  }
})
</script>

<style lang="scss" scoped>
$types: ('regular', 'premium', 'jumbo');
.gallery-pack-buy-popup {
  width: 85.5rem;
  height: 43.625rem;
  @each $type in $types {
    &.#{$type} {
      @include background(url('#{$path-gallery}premium/popup-body-#{$type}.avif'), contain);
      .popup-left-img {
        @include background(
          url('#{$path-gallery}premium/gallery-pack-premium-img-#{$type}.avif'),
          contain
        );
        width: 19.313rem;
        height: 21.125rem;
        top: 0.25rem;
      }
      .popup-left-info {
        top: -1rem;
      }
      .popup-left-box {
        width: 16.25rem;
        height: 4rem;
        border-style: solid;
        border-width: 0.125rem;
        border-image-slice: 1;
        @if $isSsm {
          background-color: #363e5d;
          border-image-source: linear-gradient(to top, #fff284, #f6c717);
          box-shadow:
            inset -0.188rem 0 2.5rem 0 rgba(6, 34, 55, 0.48),
            inset -0.0188rem -0.188rem 2.5rem 0 rgba(6, 34, 55, 0.48);
        }
        @if $isWsm {
          background-color: #0f3251;
          border-image-source: linear-gradient(to top, #fff284, #f6c717);
          box-shadow:
            inset -0.188rem 0 2.5rem 0 rgba(51, 123, 214, 0.19),
            inset -0.0188rem -0.188rem 2.5rem 0 rgba(6, 34, 55, 0.48);
        }
      }
    }
  }
  .popup-buy-box {
    width: 51.875rem;
    height: 7.5rem;
    border-style: solid;
    border-width: 0.125rem;
    border-image-slice: 1;
    border-image-source: linear-gradient(to top, #f6c717, #fff284);
    background-image: linear-gradient(to right, #1d1f2c, #2e3c63, #1d1f2c);
    padding: 0.938rem 1.688rem;
    @if $isWsm {
      border-image-source: #fadd1e;
      background-image: linear-gradient(to right, #18334e, #215d84, #18334e);
    }
    @if $isSsm {
      border-image-source: linear-gradient(to top, #f6c717, #fff284);
      background-image: linear-gradient(to right, #1d1f2c, #2e3c63, #1d1f2c);
    }
    &-old-price {
      position: absolute;
      height: 4rem;
      top: -0.5rem;
      right: 11rem;
      &:before {
        content: '';
        position: absolute;
        background: #b52e2d;
        width: 7.8125rem;
        height: 0.25rem;
        left: 49%;
        transform: translate(-50%) skewY(-12deg);
        top: 45%;
      }
    }
    &-best-value {
      @include background(url('#{$path-gallery}premium/best-value.avif?v1'), contain);
      width: 17.375rem;
      height: 2.875rem;
      padding-left: 1.5rem;
      @if $isWsm {
        padding-left: 2.5rem;
      }
    }
    &-discount {
      width: 8.625rem;
      height: 2.625rem;
      background-image: linear-gradient(to right, #ed1616, #ff3f3f);
      right: -1.75rem;
    }
    &-value {
      position: relative;
      top: -0.5rem;
      width: 25rem;
      p {
        background-clip: text;
        text-shadow: none;
        color: transparent;

        @if $isWsm {
          background-image: linear-gradient(to top, #bdbdbd 13%, #ffffff 100%);
        }

        @if $isSsm {
          background-image: linear-gradient(to top, #d7d7d7 13%, #ffffff 100%);
        }
      }
    }
    #buy-gallery-pack {
      right: -1rem;
      position: absolute;
    }
  }
}
</style>
